/* Session autosave — opt-in background persistence after every interaction.
 *
 * Two independent toggles, both off by default:
 *
 *   - CLOUD autosave  → after every fan-out / synthesis / deliverable, push
 *     the current snapshot to the user's cloud history. Uses a deterministic
 *     name ("Auto-save · YYYY-MM-DD") so subsequent writes OVERWRITE the
 *     same blob instead of filling quota with one blob per click. The
 *     date roll-over creates a fresh blob each day, which doubles as a
 *     coarse daily checkpoint history.
 *
 *   - LOCAL autosave  → every interaction also writes to a user-chosen file
 *     on disk via the File System Access API. The file handle is held in
 *     memory for the lifetime of the page; first autosave prompts the
 *     user to pick the file, every subsequent autosave is silent. Falls
 *     back to a one-time download if the API is unavailable.
 *
 * Triggers are debounced (~600ms) — a fan-out followed by a synthesis
 * fires both points, but only one actual save lands, with the freshest
 * state.
 */

import { captureSnapshot } from './session-io';
import { saveBlob, BlobApiError } from './cloud-blobs';
import { getUser } from '../auth';
import { toast } from './toast';
import { t } from './i18n';

const LS_CLOUD = 'lp_aiboard_autosave_cloud';
const LS_LOCAL = 'lp_aiboard_autosave_local';
const KIND = 'session';
const DEBOUNCE_MS = 600;

// ---------- Settings ----------

export function isCloudAutosaveEnabled(): boolean {
  try { return localStorage.getItem(LS_CLOUD) === '1'; } catch { return false; }
}

export function isLocalAutosaveEnabled(): boolean {
  try { return localStorage.getItem(LS_LOCAL) === '1'; } catch { return false; }
}

export function setCloudAutosaveEnabled(on: boolean): void {
  try {
    if (on) localStorage.setItem(LS_CLOUD, '1');
    else    localStorage.removeItem(LS_CLOUD);
  } catch {}
}

export function setLocalAutosaveEnabled(on: boolean): void {
  try {
    if (on) localStorage.setItem(LS_LOCAL, '1');
    else {
      localStorage.removeItem(LS_LOCAL);
      _localHandle = null;  // forget the picked file when the user turns it off
    }
  } catch {}
}

// ---------- Local file handle (in-memory for the page lifetime) ----------

let _localHandle: any = null;  // FileSystemFileHandle if the API is available
let _localPromptedThisSession = false;

function hasFileSystemAccessAPI(): boolean {
  return typeof (window as any).showSaveFilePicker === 'function';
}

function defaultFilename(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `aiboard-autosave-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.aiboard.json`;
}

async function ensureLocalHandle(): Promise<any | null> {
  if (_localHandle) return _localHandle;
  if (!hasFileSystemAccessAPI()) return null;
  if (_localPromptedThisSession) return null;  // user cancelled before; don't badger
  _localPromptedThisSession = true;
  try {
    const handle = await (window as any).showSaveFilePicker({
      suggestedName: defaultFilename(),
      types: [{
        description: 'AI Board session',
        accept: { 'application/json': ['.json', '.aiboard.json'] },
      }],
    });
    _localHandle = handle;
    return handle;
  } catch (err: any) {
    if (err?.name !== 'AbortError') console.warn('[autosave] file picker failed', err);
    return null;
  }
}

async function writeLocal(snapJson: string): Promise<boolean> {
  if (!hasFileSystemAccessAPI()) {
    // Fallback path can't silently update a file — once-per-page download
    // would spam the user. Tell them once that local autosave needs Chrome
    // or Edge and disable the toggle.
    toast(t('autosave.local_unsupported') || 'Local autosave needs Chrome or Edge. Use cloud autosave or save manually.');
    setLocalAutosaveEnabled(false);
    return false;
  }
  const handle = await ensureLocalHandle();
  if (!handle) return false;
  try {
    const writable = await handle.createWritable();
    await writable.write(snapJson);
    await writable.close();
    return true;
  } catch (err: any) {
    console.warn('[autosave] local write failed', err);
    return false;
  }
}

// ---------- Cloud ----------

function cloudAutosaveName(): string {
  // Date-stamped so a long-running session rolls to a fresh blob daily
  // (daily snapshot history); within a single day all writes overwrite
  // the same blob.
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `Auto-save · ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

async function writeCloud(snap: ReturnType<typeof captureSnapshot>): Promise<boolean> {
  const u = getUser();
  if (!u) return false;  // silently no-op when not signed in
  try {
    await saveBlob(KIND, cloudAutosaveName(), snap as unknown as Record<string, unknown>);
    return true;
  } catch (err) {
    if (err instanceof BlobApiError && err.isQuotaExceeded) {
      toast(t('autosave.cloud_quota') || 'Cloud autosave paused: storage quota reached.');
      // Don't disable the toggle — quota may free up; subsequent attempts
      // will surface the toast again.
      return false;
    }
    if (err instanceof BlobApiError && err.isUnauthorized) {
      // Session expired; let manual sign-in re-enable.
      return false;
    }
    console.warn('[autosave] cloud write failed', err);
    return false;
  }
}

// ---------- Trigger (debounced) ----------

let _timer: number | null = null;
let _inFlight = false;
let _pending = false;

export function triggerAutosave(): void {
  if (!isCloudAutosaveEnabled() && !isLocalAutosaveEnabled()) return;
  // Debounce: a fan-out finishing right before synthesis fires both
  // hooks — coalesce into a single write with the freshest state.
  if (_timer) window.clearTimeout(_timer);
  _timer = window.setTimeout(runAutosave, DEBOUNCE_MS);
}

async function runAutosave(): Promise<void> {
  _timer = null;
  if (_inFlight) { _pending = true; return; }
  _inFlight = true;
  try {
    const snap = captureSnapshot();
    // Don't autosave an empty workspace — wait until there's actual content.
    if (!snap.prompt && (snap.responses || []).length === 0) return;
    const json = JSON.stringify(snap, null, 2);

    const cloudOn = isCloudAutosaveEnabled();
    const localOn = isLocalAutosaveEnabled();

    // Fire both in parallel — they're independent.
    await Promise.all([
      cloudOn ? writeCloud(snap) : Promise.resolve(false),
      localOn ? writeLocal(json) : Promise.resolve(false),
    ]);
  } finally {
    _inFlight = false;
    if (_pending) {
      _pending = false;
      // A new trigger came in while we were saving — run again with
      // the latest state.
      window.setTimeout(runAutosave, 0);
    }
  }
}

// ---------- One-shot eager save (used by the settings drawer "Save now") ----------

export async function saveNow(): Promise<{ cloud: boolean; local: boolean }> {
  if (_timer) { window.clearTimeout(_timer); _timer = null; }
  const snap = captureSnapshot();
  if (!snap.prompt && (snap.responses || []).length === 0) return { cloud: false, local: false };
  const json = JSON.stringify(snap, null, 2);
  const [cloud, local] = await Promise.all([
    isCloudAutosaveEnabled() ? writeCloud(snap) : Promise.resolve(false),
    isLocalAutosaveEnabled() ? writeLocal(json) : Promise.resolve(false),
  ]);
  return { cloud, local };
}
